import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { reduxForm, InjectedFormProps, SubmissionError } from 'redux-form'
// hooks
import { useModalData } from 'hooks/index'
// components
import { Modal } from 'components/UI'
import { Header } from 'components/Modals/ScaleOptionModal/Create'
import { CloseIcon } from 'components/UI/Icons'
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { Button, BUTTON_LAYOUTS, CREATE_BUTTON_STYLE, ErrorMessage } from 'components/UI'
// constants
import { Modals } from 'constants/modals'
import { Forms } from 'constants/forms'
import { PAPER_PROPS } from './constants'
// graphql
import { AUTH_TWO_FACTOR_DISABLE_MUTATION } from 'graphql/auth/twoFactor/mutations'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
// views
import { FormContainer, Divider, ButtonGroup } from './views'

type FormData = {
  code?: string
  password?: string
}

const TwoFactorDisableForm: React.FC<InjectedFormProps<FormData>> = ({ handleSubmit, error, reset }) => {
  const {
    hideModal,
    modalData: { refetch }
  } = useModalData({ modalType: Modals.TWO_FACTOR_DISABLE_MODAL })

  const [disableMutation, { loading }] = useMutation(AUTH_TWO_FACTOR_DISABLE_MUTATION)

  const handleDisable = async (formData: FormData) => {
    try {
      await disableMutation({
        variables: {
          code: formData.code?.trim(),
          password: formData.password?.trim()
        }
      })

      if (refetch) {
        refetch()
      }

      reset()
      hideModal()
    } catch (errors) {
      throw new SubmissionError({
        _error: parseGraphQLErrorMessages(errors)
      })
    }
  }

  const onCancel = () => {
    reset()
    hideModal()
  }

  return (
    <FormContainer onSubmit={handleSubmit(handleDisable)}>
      <p>To disable two-factor authentication, enter either your current TOTP code or your password.</p>

      <ReduxInput type='text' name='code' label='TOTP Code (6 digits)' inputProps={{ autoComplete: 'new-password' }} />
      <Divider>OR</Divider>
      <ReduxInput type='password' name='password' label='Password' inputProps={{ autoComplete: 'new-password' }} />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonGroup>
        <Button layout={BUTTON_LAYOUTS.WHITE} onClick={onCancel} type='button'>
          Cancel
        </Button>

        <Button layout={BUTTON_LAYOUTS.PURPLE} type='submit' disabled={loading} style={CREATE_BUTTON_STYLE}>
          Disable 2FA
        </Button>
      </ButtonGroup>
    </FormContainer>
  )
}

const TwoFactorDisableFormWrapped = reduxForm<FormData>({
  form: Forms.TWO_FACTOR_DISABLE_FORM
})(TwoFactorDisableForm)

const TwoFactorDisableModal: React.FC = () => {
  const {
    hideModal,
    modalData: { isOpen }
  } = useModalData({ modalType: Modals.TWO_FACTOR_DISABLE_MODAL })

  return (
    <Modal width='xs' isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Disable Two-Factor Authentication</span>
        <CloseIcon right={0} onClick={hideModal} />
      </Header>
      <TwoFactorDisableFormWrapped />
    </Modal>
  )
}

export default TwoFactorDisableModal
